import React, { useState, useEffect } from 'react'
import { useUpdateTaskResources } from '../../hooks/useTasks'
import type { Task } from '../../hooks/useTasks'

interface TaskResourceFormProps {
  projectId: number
  task: Task
  onClose: () => void
  onSuccess?: () => void
}

export const TaskResourceForm: React.FC<TaskResourceFormProps> = ({
  projectId,
  task,
  onClose,
  onSuccess
}) => {
  const [resources, setResources] = useState({
    personil: task.resources?.personil || 0,
    equipment: task.resources?.equipment || [],
    budget: task.resources?.budget || 0
  })
  const [equipmentInput, setEquipmentInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { mutate: updateTaskResources, isPending } = useUpdateTaskResources(projectId)
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    updateTaskResources(
      { taskId: task.id, resources },
      {
        onSuccess: () => {
          onSuccess?.()
          onClose()
        },
        onSettled: () => setIsSubmitting(false)
      }
    )
  }
  
  const addEquipment = () => {
    if (equipmentInput.trim() && !resources.equipment.includes(equipmentInput.trim())) {
      setResources({
        ...resources,
        equipment: [...resources.equipment, equipmentInput.trim()]
      })
      setEquipmentInput('')
    }
  }
  
  const removeEquipment = (item: string) => {
    setResources({
      ...resources,
      equipment: resources.equipment.filter(e => e !== item)
    })
  }
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addEquipment()
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Assign Resources</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
              <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{task.taskName}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personil (people)</label>
              <input
                type="number"
                min="0"
                value={resources.personil}
                onChange={(e) => setResources({...resources, personil: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Number of people"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Equipment</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={equipmentInput}
                  onChange={(e) => setEquipmentInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add equipment (e.g., excavator)"
                />
                <button
                  type="button"
                  onClick={addEquipment}
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add
                </button>
              </div>
              {resources.equipment.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {resources.equipment.map((item) => (
                    <span key={item} className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {item}
                      <button
                        type="button"
                        onClick={() => removeEquipment(item)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget (IDR)</label>
              <input
                type="number"
                min="0"
                value={resources.budget}
                onChange={(e) => setResources({...resources, budget: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Budget in IDR"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isPending}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save Resources'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

TaskResourceForm.displayName = 'TaskResourceForm'